import { Hidden } from '@material-ui/core';
import { FormikProps } from 'formik';
import React from 'react';
import { ContributionAmountType, ContributionType, LayDcPlanAdoptionContentModel, LayDcPlanFormModel, ParticipantModel } from '../models/plan-adoption-form-models';
import { QccoPlanAdoptionWizard } from '../wizards/qcco-plan-adoption-wizard';
import { buttonStyles } from '../../../../../../global/constants/theme/styles/components/buttonstyles';
import { NumberField, NumberInputTypes } from '../../../../../../global/components/form-element/number-field';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { getCurrencyFormat } from '../../../../../../global/helpers/format-helpers';

interface Props {
    fieldName: string,
    participant: ParticipantModel,
    formProps: FormikProps<LayDcPlanFormModel>,
    index: number;
    wizard: QccoPlanAdoptionWizard;
    onRemove?: (formikProps: FormikProps<LayDcPlanFormModel>, participant: ParticipantModel) => void;
    onContributionTypeChanged?: (formikProps: FormikProps<LayDcPlanFormModel>, participant: ParticipantModel, newContributionType: ContributionType) => void;
    isReadonly?: boolean;
}
export function PlanParticipantRowView(props: Props) {

    const buttonClasses = buttonStyles();
    const { fieldName, participant, formProps, index, wizard, onRemove, isReadonly, onContributionTypeChanged } = props;

    const employerContributionStatus = formProps?.values?.employer_contribution_status;

    const content: LayDcPlanAdoptionContentModel = wizard?.getFormContentModel();
    const contributionTypeOptions = wizard?.getContributionTypeOptions(employerContributionStatus);
    const labelSettings: LabelSettingProps = wizard?.epiContent?.labelSettingState?.epiContent;

    if (!participant) return null;

    function onRowRemove() {
        if (onRemove) {
            onRemove(formProps, participant);
        }
    }

    function onRowContributionTypeChanged(e) {
        const { value } = e.target;

        if (onContributionTypeChanged) {
            onContributionTypeChanged(formProps, participant, value);
        }
    }

    function renderName() {

        if (isReadonly) {
            return (
                <div>
                    {participant.participant_name}
                </div>
            );
        }

        return (
            <FormField
                name={`${fieldName}.${index}.participant_name`}
                component='input'
                touched={formProps.touched}
                errors={formProps.errors}
            />
        );
    }

    function renderReadonlyContributionAmount() {
        if (!isReadonly) return null;

        if (participant?.contribution_value_type === ContributionAmountType.Percentage) {
            return (
                <div>
                    {participant.contribution_percentage}%
                </div>
            );
        }

        return (
            <div>
                {getCurrencyFormat(participant.contribution_amount, labelSettings)}
            </div>
        );
    }

    function renderContributionType() {

        const hasOtherField = participant?.contribution_type === ContributionType.Other;

        if (isReadonly) {
            const contributionTypeText = contributionTypeOptions?.find((option) => option?.value === participant?.contribution_type)?.text;
            return (
                <>
                    <div>
                        {contributionTypeText ?? ''}
                    </div>
                    {
                        hasOtherField &&
                        <div>
                            {participant?.contribution_other ?? ''}
                        </div>
                    }
                </>
            );
        }

        return (
            <>
                <FormField
                    name={`${fieldName}.${index}.contribution_type`}
                    options={contributionTypeOptions}
                    component='input'
                    touched={formProps.touched}
                    errors={formProps.errors}
                    placeholder={labelSettings?.selectDefaultText}
                    onChange={(e) => onRowContributionTypeChanged(e)}
                    select
                />
                {
                    hasOtherField &&
                    <FormField
                        name={`${fieldName}.${index}.contribution_other`}
                        component='input'
                        touched={formProps.touched}
                        errors={formProps.errors}
                    />
                }
            </>
        );
    }

    function renderContributionAmount() {

        if (isReadonly) {
            return renderReadonlyContributionAmount();
        }

        if (participant?.contribution_value_type === ContributionAmountType.Percentage) {
            return (
                <NumberField
                    name={`${fieldName}.${index}.contribution_percentage`}
                    label=''
                    type={NumberInputTypes.PercentageInteger}
                    formikProps={formProps}
                />
            );
        }

        if ((participant?.contribution_value_type === ContributionAmountType.Dollar) ||
            (participant?.contribution_value_type === ContributionAmountType.Other)) {
            return (
                <NumberField
                    name={`${fieldName}.${index}.contribution_amount`}
                    label=''
                    type={NumberInputTypes.Currency}
                    formikProps={formProps}
                />
            );
        }

        return (
            <NumberField
                name={`${fieldName}.${index}.contribution_amount`}
                label=''
                type={NumberInputTypes.Integer}
                formikProps={formProps}
                disabled
            />
        );
    }


    function renderAction() {
        if (!onRemove) return null;

        return (<button className={buttonClasses.linkButton} type='button' onClick={onRowRemove} key={participant?.id}><strong>{content?.contributionRemoveLabel}</strong></button>);
    }

    return (
        <React.Fragment key={participant?.id}>
            <div className='plan-adoption-table-row'>
                <div className='plan-adoption-table-row-cell plan-adoption-table-row-cell-name'>
                    <Hidden smUp>
                        <p className='input-label'>{content?.participantNameLabel}</p>
                    </Hidden>
                    <div>
                        {renderName()}
                    </div>
                    <Hidden mdUp>
                        {renderAction()}
                    </Hidden>
                </div>
                <div className='plan-adoption-table-row-cell'>
                    <Hidden smUp>
                        <p className='input-label'>{content?.contributionTypeLabel}</p>
                    </Hidden>
                    <div>
                        {renderContributionType()}
                    </div>
                </div>
                <div className='plan-adoption-table-row-cell'>
                    <Hidden smUp>
                        <p className='input-label'>{content?.contributionAmountLabel}</p>
                    </Hidden>
                    <div>
                        {renderContributionAmount()}
                    </div>
                </div>

                {
                    !isReadonly &&
                    <Hidden smDown>
                        <div className='plan-adoption-table-row-cell'>
                            {renderAction()}
                        </div>
                    </Hidden>
                }
            </div>
        </React.Fragment>
    );
}

PlanParticipantRowView.defaultProps = {
    onRemove: null,
    onContributionTypeChanged: null,
    isReadonly: false,
};